import type { PushPlatform, PushProvider } from '../types';

/**
 * A workspace environment advertises its configured push setups as a list of
 * provider keys. Each key resolves to the provider behind it, the platforms it
 * can send to, and whether it sends through a development (sandbox) endpoint.
 */
export type PushProviderDetails = {
  provider: PushProvider;
  platform: readonly PushPlatform[];
  /** Whether the provider has a sandbox endpoint the workspace can opt into. */
  allowDevelopment: boolean;
  /** Whether this key is the sandbox flavour of its provider. */
  development: boolean;
};

export const PUSH_PROVIDERS = {
  fcm: { provider: 'fcm', platform: ['android'], allowDevelopment: false, development: false },
  fcm_ios: { provider: 'fcm', platform: ['android', 'ios'], allowDevelopment: false, development: false },
  apns: { provider: 'apns', platform: ['ios'], allowDevelopment: true, development: false },
  apns_sandbox: { provider: 'apns', platform: ['ios'], allowDevelopment: true, development: true },
} as const satisfies Record<string, PushProviderDetails>;

export type PushProviderKey = keyof typeof PUSH_PROVIDERS;

/** One configured platform: the provider details, keyed by the platform itself. */
export type PushPlatformDetails = PushProviderDetails & { key: PushPlatform };

export const PLATFORM_LABELS = {
  ios: 'iOS',
  android: 'Android',
} satisfies Record<PushPlatform, string>;

/** Keyed by the raw strings an environment lists, so an unknown key simply misses. */
const PROVIDER_DETAILS = new Map<string, PushProviderDetails>(Object.entries(PUSH_PROVIDERS));

/**
 * Expand a workspace's `push_providers` into one entry per platform it can send
 * to. A provider that serves both platforms (`fcm_ios`) yields an entry for each,
 * so callers can ask "is iOS configured, and through what?" with a single lookup.
 *
 * Unrecognised keys are skipped rather than throwing, so a provider key added to
 * the backend ahead of the UI degrades to "that platform is not configured".
 */
export function derivePushPlatforms(pushProviders: readonly string[] | null | undefined): PushPlatformDetails[] {
  return (pushProviders ?? []).flatMap((key) => {
    const details = PROVIDER_DETAILS.get(key);
    if (details == null) {
      return [];
    }
    return details.platform.map((platform) => ({ ...details, key: platform }));
  });
}

/** The distinct providers a workspace currently sends push through. */
export function derivePushProviders(pushProviders: readonly string[] | null | undefined): PushProvider[] {
  const providers = (pushProviders ?? []).flatMap((key) => {
    const details = PROVIDER_DETAILS.get(key);
    return details == null ? [] : [details.provider];
  });
  return [...new Set(providers)];
}

/** The details for one platform, or `undefined` when it is not configured. */
export function findPushPlatform(
  platforms: readonly PushPlatformDetails[],
  platform: PushPlatform,
): PushPlatformDetails | undefined {
  return platforms.find((item) => item.key === platform);
}
